/**
 * Workspaces (vfs-service docs/workspaces.md): groups of projects with their own members and brain, kept by the
 * sandbox gateway (src/gateway/workspaces/routes.js) under the vfs path the web app already uses, with the person's
 * authorized fetch:
 * - `GET /api/v1/workspaces` -> `{ workspaces: [{ id, name, description, owner, role, projectCount, memberCount, projectIds, hasBrain }] }`;
 * - `POST /api/v1/workspaces` `{ name, description? }` -> the new workspace;
 * - `GET|PATCH|DELETE /api/v1/workspaces/:id` (GET adds `members` and `projects`, each project with the caller's `role`);
 * - `PUT|DELETE /api/v1/workspaces/:id/members/:email` (`{ role }`) -> `{ members }`;
 * - `PUT|DELETE /api/v1/workspaces/:id/projects/:projectId` -> `{ projectId, workspace, workspaces }`: adds the project to
 *   this workspace, or takes it out of this one only (a project may be in several; `workspaces` are all of them after);
 * - `GET /api/v1/workspaces/of-project/:projectId` -> `{ workspaces: [{ id, name, role }] }` (its owner sees all of them,
 *   anyone else the ones they are in);
 * - `GET /api/v1/workspaces/:id/brain/tree`, `GET|PUT|DELETE /api/v1/workspaces/:id/brain/file?path=`.
 * An error carries `status` and the gateway's `code` (e.g. 'owner_only', 'read_only'). A gateway without workspaces
 * (older, or shared mode: 404 on the list) resolves the list to null, and the web app then shows none.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const seg = (value) => encodeURIComponent(String(value));

async function failure(response, fallback) {
  const data = await response.json().catch(() => null);
  const error = new Error(data?.error || `${fallback} (${response.status})`);
  error.status = response.status;
  if (data?.code) { error.code = data.code; }
  return error;
}

async function call(path, { method = 'GET', body, fallback = 'The workspace request failed' } = {}) {
  const response = await getAuthorizedFetch()(`${base()}/api/v1/workspaces${path}`, {
    method,
    headers: { Accept: 'application/json', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) { throw await failure(response, fallback); }
  return response.json();
}

/** The person's workspaces; null where the gateway has none. */
export async function listWorkspaces() {
  if (!base()) { return null; }
  try {
    return (await call('')).workspaces || [];
  } catch (error) {
    if (error.status === 404) { return null; }
    throw error;
  }
}

export const getWorkspace = (id) => call(`/${seg(id)}`);
export const createWorkspace = ({ name, description = '' }) => call('', { method: 'POST', body: { name, description } });
export const updateWorkspace = (id, patch) => call(`/${seg(id)}`, { method: 'PATCH', body: patch });
export const deleteWorkspace = (id) => call(`/${seg(id)}`, { method: 'DELETE' });

export const putWorkspaceMember = (id, email, role) => call(`/${seg(id)}/members/${seg(String(email).trim().toLowerCase())}`, { method: 'PUT', body: { role } });
export const removeWorkspaceMember = (id, email) => call(`/${seg(id)}/members/${seg(email)}`, { method: 'DELETE' });

/** Adds one of the person's own projects to the workspace (it stays in its other workspaces). */
export const addProjectToWorkspace = (id, projectId) => call(`/${seg(id)}/projects/${seg(projectId)}`, { method: 'PUT' });
/** Takes a project out of this workspace only. */
export const removeProjectFromWorkspace = (id, projectId) => call(`/${seg(id)}/projects/${seg(projectId)}`, { method: 'DELETE' });

/** The workspaces a project is in, by name (`[{ id, name, role }]`); null where the gateway has no workspaces. */
export async function getProjectWorkspaces(projectId) {
  if (!base() || !projectId) { return null; }
  try {
    return (await call(`/of-project/${seg(projectId)}`)).workspaces || [];
  } catch (error) {
    if (error.status === 404) { return null; }
    throw error;
  }
}

/** Tells every open view of a project's workspaces (the header's chips, Home) that they changed. */
export const WORKSPACES_CHANGED = 'flow:projectWorkspacesChanged';
export function announceWorkspacesChanged(projectId) {
  if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent(WORKSPACES_CHANGED, { detail: { projectId } })); }
}

/** The brain's files as paths ("memory/x.md"), sorted; [] while it is empty. */
export async function listBrainFiles(id) {
  const { tree } = await call(`/${seg(id)}/brain/tree`);
  const out = [];
  const walk = (node, prefix) => {
    (node?.children || []).forEach((child) => {
      const path = prefix ? `${prefix}/${child.name}` : child.name;
      if (child.type === 'directory') { walk(child, path); } else { out.push(path); }
    });
  };
  walk(tree, '');
  return out.sort();
}
export const readBrainFile = (id, path) => call(`/${seg(id)}/brain/file?path=${seg(path)}`);
export const writeBrainFile = (id, path, content) => call(`/${seg(id)}/brain/file?path=${seg(path)}`, { method: 'PUT', body: { content } });
export const deleteBrainFile = (id, path) => call(`/${seg(id)}/brain/file?path=${seg(path)}`, { method: 'DELETE' });

/** Who may do what, from the person's role in the workspace. */
export const canManage = (workspace) => workspace?.role === 'owner';
export const canEdit = (workspace) => workspace?.role === 'owner' || workspace?.role === 'editor';

/**
 * The person's own projects, as the gateway's index lists them (`GET /api/v1/projects?owner=<sub>`): the ones a
 * workspace's "Add project" offers. `[{ projectId, name, workspaces }]` (the workspaces it is already in).
 */
export async function listMyProjects(sub) {
  const response = await getAuthorizedFetch()(`${base()}/api/v1/projects?owner=${seg(sub)}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) { throw await failure(response, 'Your projects could not be listed'); }
  const { projects = [] } = await response.json();
  return projects.filter((project) => !project.role).map(({ projectId, name, workspace, workspaces }) => ({
    projectId, name: name || projectId, workspaces: workspaces || (workspace ? [workspace] : []),
  }));
}
